import { type TimelineFeatures, useFeatures } from "@expert/features";
import { fallbackGaiaPayloadProps, getFallbackPartner, useGaiaWebsocket } from "@expert/gaia";
import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs, OrchestratorInvokeMessagePayload } from "@expert/shared-types";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { useTimelineStore } from "../state";

export function useRequestAutoCompleteSuggestions(logger: Logger, gaiaPayloadProps: GaiaPayloadConfigs) {
    const websocketObj = useGaiaWebsocket();
    const { dispatcher } = useReactAnalytics();
    const { expertId, currentSessionId: sessionId, getTimelineState } = useTimelineStore();
    const { callSid, chatId, partner } = getTimelineState(sessionId);
    const {
        features: { isAutoCompleteEnabled },
    } = useFeatures<TimelineFeatures>();

    // Currently this should keep the behavior disabled for chat. When we want to enable chat, we would update this to check for callSid or chatId.
    // We could also use a separate isAutoCompleteEnabledForChat flag to keep the behavior separate from the voice flag. But if we don't need to
    // manage chat behavior separately, we can just use the existing isAutoCompleteEnabled flag.
    if (!isAutoCompleteEnabled || !callSid || !sessionId) {
        return {};
    }

    const { autoCompleteAppName } = gaiaPayloadProps ?? fallbackGaiaPayloadProps(chatId ? "chat" : "voice");
    const requestAutoCompleteSuggestions = (textInput: string, isProactiveSuggestion = false) => {
        const fetchAutoCompleteSuggestionsPayload: OrchestratorInvokeMessagePayload = {
            action: "orchestrator-invoke",
            appName:
                autoCompleteAppName ?? (chatId ? "expert-assist-messaging-autocomplete" : "expert-assist-autocomplete"),
            correlationId: crypto.randomUUID(),
            message: textInput,
            messageId: crypto.randomUUID(),
            messageType: "AutoComplete",
            partner: getFallbackPartner({ partner }),
            sessionId: callSid,
            sessionGroupId: sessionId,
            userId: expertId,
            userType: "expert",
        };
        logger
            .child({ module: "useRequestAutoCompleteSuggestions" })
            .info({ input: textInput, isProactiveSuggestion }, "AutoComplete | suggestion requested");
        void dispatcher.dispatchBusinessEvent("AutoComplete_SuggestionRequested", {
            input: textInput,
            isProactiveSuggestion,
        });

        websocketObj?.sendJsonMessage(fetchAutoCompleteSuggestionsPayload);
    };

    return { requestAutoCompleteSuggestions };
}
